import { useCallback, useState } from 'react';

/**
 * Paginación por cursor de las tablas y listas del panel (G121, ADR-0047; convenciones de API §9): «Anterior» y
 * «Siguiente», sin total ni números de página. «Siguiente» pide la página con el `next_cursor` de la respuesta;
 * «Anterior» vuelve con los cursores ya recorridos, que se guardan aquí, en memoria: vuelve a la página exacta ya vista.
 * Donde haga falta una cifra, una tarjeta de cifra con su propia operación. Sin estilos: el componente que pinta los
 * dos botones llega con la exportación de Claude Code (G127, ADR-0050).
 */
export interface Recorrido {
    /** Cursor con el que se pidió cada página ya vista; la primera, sin cursor (null). */
    cursores: readonly (string | null)[];
    /** Página que se ve ahora (posición en `cursores`). */
    pagina: number;
}

export const PRIMERA_PAGINA: Recorrido = { cursores: [null], pagina: 0 };

/** Cursor para pedir la página que se ve (`?cursor=`); null en la primera. */
export function cursorDe(recorrido: Recorrido): string | null {
    return recorrido.cursores[recorrido.pagina] ?? null;
}

export function hayAnterior(recorrido: Recorrido): boolean {
    return recorrido.pagina > 0;
}

/** Hay página siguiente si la respuesta trae `next_cursor` (nulo en la última página). */
export function haySiguiente(siguienteCursor: string | null | undefined): boolean {
    return typeof siguienteCursor === 'string' && siguienteCursor !== '';
}

/** Avanza con el `next_cursor` de la página que se ve; lo que hubiera por delante se olvida (la lista pudo cambiar). */
export function avanzar(recorrido: Recorrido, siguienteCursor: string | null | undefined): Recorrido {
    if (!haySiguiente(siguienteCursor)) {
        return recorrido;
    }
    return { cursores: [...recorrido.cursores.slice(0, recorrido.pagina + 1), siguienteCursor ?? null], pagina: recorrido.pagina + 1 };
}

export function retroceder(recorrido: Recorrido): Recorrido {
    return hayAnterior(recorrido) ? { ...recorrido, pagina: recorrido.pagina - 1 } : recorrido;
}

/**
 * Estado de la paginación de una lista. Uso:
 *
 *     const pag = usePaginacionCursor();
 *     useEffect(() => { ops.listMembers(negocio, { cursor: pag.cursor }).then(setLista); }, [pag.cursor]);
 *     <Paginacion hayAnterior={pag.hayAnterior} siguienteCursor={lista.next_cursor}
 *         onAnterior={pag.anterior} onSiguiente={() => pag.siguiente(lista.next_cursor)} />
 *
 * Al cambiar un filtro o el orden, `reiniciar()` vuelve a la primera página.
 */
export function usePaginacionCursor() {
    const [recorrido, setRecorrido] = useState<Recorrido>(PRIMERA_PAGINA);
    const siguiente = useCallback((siguienteCursor: string | null | undefined) => {
        setRecorrido((r) => avanzar(r, siguienteCursor));
    }, []);
    const anterior = useCallback(() => {
        setRecorrido(retroceder);
    }, []);
    const reiniciar = useCallback(() => {
        setRecorrido(PRIMERA_PAGINA);
    }, []);
    return { cursor: cursorDe(recorrido), hayAnterior: hayAnterior(recorrido), siguiente, anterior, reiniciar };
}

/**
 * La cifra de la pestaña de una de las cinco listas pequeñas del módulo 1 (Miembros, Invitaciones, Roles, Tablets y
 * Facturas): su `total_count` (G139, precisa G121). Las listas grandes no lo traen y van sin cifra.
 */
export function cifraDeLista(lista: { total_count: number } | null | undefined): number | null {
    return lista?.total_count ?? null;
}
